import React, {useState, useRef, useEffect} from 'react';
import { Box, Typography, Button, TextField, FormControl } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { useAppSelector } from '../../app/hooks';
import { selectLGInternalUser } from '../../activeScreenSlice';

type ValidationRule = {
  opco_id: string;
  validation_id: number;
  configValue: string;
  files: {
    filename: string;
    path?: string;
  };
};

const ValidationRuleLineItem = ({
  rule,
  ruleId,
  description,
  showDownload= false,
  showTolerance = false,
  showUpload = false,
  showAge= false,
  handleDownload,
  onUpdate,
  index,
  reset,
  confValue,
  showFuzzyLabel= false,
  setIsValidationError,
  isValidationError

}: {
    rule: string,
    ruleId: number,
    description: string,
    showDownload?: boolean,
    showTolerance?: boolean,
    showUpload?: boolean,
    showAge?: boolean,
    handleDownload?: () => void,
    onUpdate?: any,
    index: number,
    reset: boolean,
    confValue?: string | null,
    showFuzzyLabel?: boolean,
    setIsValidationError:(value)=>void,
    isValidationError:boolean

}) => {
  const [fleName, setFileName] = useState('');
  const [file, setFile] = useState(null);
  const [configValue, setConfigValue] = useState('');
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [errors, setErrors] = useState<{ configValue?: string }>({});
  const LGInternalUser = useAppSelector(selectLGInternalUser);

  useEffect(() => {
    // Reset input fields when reset trigger changes
    if (confValue === null) {
      setConfigValue('');
    }

    setFileName('');
    setFile(null);
    setErrors({});
  }, [reset]);

  useEffect(() => {
    // Initialize config value if not null
    if (confValue !== null) {
      setConfigValue(confValue);
    }
  }, [confValue]);
  
  const handleBrowseClick = () => {
      if (inputRef.current) {
          inputRef.current.click();
      }
  }
  const validateConfigValue = (value: string) => {
    if (value.trim() === '') {
      setIsValidationError(false);
      return '';
    }
    if (showTolerance ) {
      if (showFuzzyLabel) {
        if (!/^\d+$/.test(value.trim()) || Number(value.trim()) < 0) {
          setIsValidationError(true)
          return "Enter a valid number";
        }
      }else {
        if(isNaN(Number(value.trim())) || Number(value.trim()) < 0){
          setIsValidationError(true)
          return "Enter a valid number test";
        }
      }
    } else if (showAge) {
      // Only allow positive integers
      if (!/^\d+$/.test(value.trim())) {
        setIsValidationError(true)
        return "Enter a valid age in days.";
      }
      if (Number(value.trim()) < 0) {
        setIsValidationError(true)
        return "Age cannot be negative.";
      }
      setIsValidationError(false)
    } else {
      setIsValidationError(false)
    }
    setIsValidationError(false)
    return "";
  };

  const handleConfigChange = (event) => {
    let { value } = event.target;

    if (value.length > 1) {
      value = value.replace(/^0+/, '').trim();
      if (value === '') value = '0';
    }
    
      setConfigValue(value);
      const errorMsg = validateConfigValue(value);
      setErrors((prev) => ({ ...prev, configValue: errorMsg }));
      onUpdate(index, ruleId, value, null);

    };

   const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files[0];
        setFileName(file ? file.name : '');
        setFile(file || null);
        onUpdate(index, ruleId, null, file);

    };
    
    const handleFileInpClick = () => {
        // Clear the input value
        if (inputRef.current) {
            inputRef.current.value = null;
        }
    };

  return <Box sx={{ mb: 3 }}>
    <Box>
        <Typography variant="body1" fontWeight="bold" sx={{display: "inline-block"}}>
            Validation rule: 
        </Typography>
        <Typography variant="body1" sx={{display: "inline-block"}}>
            {rule}
        </Typography>
    </Box>
    <Box sx={{display: 'flex', mb:2}}>
        <Typography variant="body1" fontWeight="bold" sx={{display: "inline-block"}}>
            Description: 
        </Typography>
        <Typography variant="body1" sx={{display: "inline-block"}}>
            {description}
        </Typography>
    </Box>
    {showDownload && (
        <Box sx={{mb: 2}}>
            <Button variant="contained" color="secondary" onClick={handleDownload} startIcon={<FileDownloadOutlinedIcon/>}>
              Download template
            </Button>
        </Box>
    )}
    {
      showUpload && (
        <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', width: '400px' }}>
          <FormControl
            fullWidth
            size="small"
            variant="outlined"
            sx={{ height: '40px' }}
        >
            <TextField
                label="File path"
                variant="outlined"
                value={fleName}
                InputProps={{
                    readOnly: true,
                }}
                size="small"
                sx={{ minWidth: 250,height:'40px' }}
            />
            
            <input
                type="file"
                name="file"
                hidden
                accept=".xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                required
                ref={inputRef}
                onChange={handleFileChange}
                onClick={handleFileInpClick}
                
            />
        </FormControl>
        <Button sx={{marginLeft: '20px'}} variant="outlined" onClick={handleBrowseClick} startIcon={<FileUploadOutlinedIcon />} disabled={LGInternalUser}>
            Browse
        </Button>
      </Box>
      )
    }
    {(showTolerance || showAge) && (
      <TextField
        label={showAge? "Age in Days" : showFuzzyLabel? "Fuzzy Match Edit Distance" : "Tolerance %"}
        variant="outlined"
        size="small"
        name="configValue"
        value={configValue}
        onChange={handleConfigChange}
        error={!!errors.configValue}
        helperText={errors.configValue}
        sx={{ mt: 1,height:'40px' }}
        required
        onKeyDown={(e) => {
          if ((showAge || showFuzzyLabel) && (e.key === '.' || e.key === 'e' || e.key === '-' || e.key === '')) {
            e.preventDefault();
        }
  }}
      />
    )}
  </Box>
};

export default ValidationRuleLineItem;